
import Vue from 'vue';
import '@/assets/css/helpdesk/helpdesk-theme.css';
import Component from 'vue-class-component';
import {Watch} from 'vue-property-decorator';
import EmailHtmlFrame from '@/components/helpdesk/EmailHtmlFrame.vue';
import RichTextEditor from '@/components/helpdesk/RichTextEditor.vue';
import BrokenImagesNotice from '@/components/helpdesk/BrokenImagesNotice.vue';
import HelpdeskAssignee from '@/components/helpdesk/HelpdeskAssignee.vue';
import {IEmailSignature} from '@/store/modules/helpdeskEmailSignaturesModule';

@Component({name: 'HelpdeskEmailSignatures', components: {EmailHtmlFrame, RichTextEditor, BrokenImagesNotice, HelpdeskAssignee}})
export default class HelpdeskEmailSignatures extends Vue {
  loading       = false;
  dialog        = false;
  confirmDelete = false;
  saving        = false;
  valid         = false;
  search        = '';
  previewOpen   = false;
  editTarget: IEmailSignature | null = null;
  deleteTarget: IEmailSignature | null = null;
  previewTarget: IEmailSignature | null = null;
  duplicatingId: number | null = null;
  // Debounced copy of form.body: re-rendering the preview frame on every keystroke would flicker.
  previewBody   = '';
  private previewTimer: any = null;
  private subscribes: any = {};

  readonly fixHint = 'edita la firma, borra la imagen marcada y vuelve a subirla con «Insertar imagen».';

  form = { name: '', user_email: '', body: '' };

  get signatures(): IEmailSignature[] {
    return this.$store.getters['helpdeskEmailSignaturesModule/signatures'] || [];
  }

  // The list is small and fully loaded: filter here instead of asking the API.
  get filtered(): IEmailSignature[] {
    const q = (this.search || '').trim().toLowerCase();
    if (!q) return this.signatures;
    return this.signatures.filter((s) => (s.name + ' ' + s.user_email).toLowerCase().indexOf(q) !== -1);
  }

  get currentUserEmail(): string {
    const u = this.$store.getters['authModule/userData'];
    return (u && u.email) ? u.email : '';
  }

  created() {
    this.subscribes = (this as any).$ons({
      'backend.call.OK.signatureCreated': () => {
        this.saving = false;
        this.duplicatingId = null;
        this.dialog = false;
        this.load();
      },
      'backend.call.KO.signatureCreated': () => { this.saving = false; this.duplicatingId = null; },
      'backend.call.OK.signatureUpdated': () => {
        this.saving = false;
        this.dialog = false;
        this.editTarget = null;
        this.load();
      },
      'backend.call.KO.signatureUpdated': () => { this.saving = false; },
      'backend.call.OK.signatureDeleted': () => {
        this.deleteTarget = null;
        this.load();
      },
      'backend.call.KO.signatureDeleted': () => {
        this.confirmDelete = false;
        this.deleteTarget = null;
      },
    });
    this.load();
  }

  beforeDestroy() { (this as any).$offs(this.subscribes); }

  @Watch('form.body', {immediate: true})
  onBodyChanged(body: string) {
    clearTimeout(this.previewTimer);
    this.previewTimer = setTimeout(() => { this.previewBody = body || ''; }, 300);
  }

  isMine(sig: IEmailSignature): boolean {
    return !!this.currentUserEmail && (sig.user_email || '').toLowerCase() === this.currentUserEmail.toLowerCase();
  }

  private load() {
    this.loading = true;
    this.$store.dispatch('helpdeskEmailSignaturesModule/fetchAll')
      .then(() => { this.loading = false; })
      .catch(() => { this.loading = false; });
  }

  openCreate() {
    this.editTarget = null;
    // Most people create their own signature.
    this.form = { name: '', user_email: this.currentUserEmail, body: '' };
    this.saving = false;
    this.dialog = true;
  }

  openEdit(sig: IEmailSignature) {
    this.editTarget = sig;
    this.form = { name: sig.name, user_email: sig.user_email, body: sig.body };
    this.saving = false;
    this.dialog = true;
  }

  openPreview(sig: IEmailSignature) {
    this.previewTarget = sig;
    this.previewOpen = true;
  }

  // A variant (another phone, a campaign banner...) usually starts from an existing signature.
  duplicate(sig: IEmailSignature) {
    if (this.duplicatingId !== null) return;
    this.duplicatingId = sig.id;
    this.$store.dispatch('helpdeskEmailSignaturesModule/create', {
      name: 'Copia de ' + sig.name,
      user_email: sig.user_email,
      body: sig.body,
    });
  }

  confirmDel(sig: IEmailSignature) {
    this.deleteTarget = sig;
    this.confirmDelete = true;
  }

  doDelete() {
    if (!this.deleteTarget) return;
    this.confirmDelete = false;
    this.$store.dispatch('helpdeskEmailSignaturesModule/delete', this.deleteTarget.id);
  }

  onSave() {
    const formRef = this.$refs.form as any;
    if (!formRef.validate()) return;
    this.saving = true;
    const data = { name: this.form.name, user_email: this.form.user_email, body: this.form.body };
    if (this.editTarget) {
      this.$store.dispatch('helpdeskEmailSignaturesModule/update', {id: this.editTarget.id, data});
    } else {
      this.$store.dispatch('helpdeskEmailSignaturesModule/create', data);
    }
  }

  relativeDate(date: string): string {
    if (!date) return '—';
    const d = new Date(date);
    const days = Math.floor((new Date().setHours(0, 0, 0, 0) - new Date(d).setHours(0, 0, 0, 0)) / 86400000);
    if (days <= 0) return 'hoy';
    if (days === 1) return 'ayer';
    if (days < 7) return 'hace ' + days + ' días';
    return 'el ' + d.toLocaleDateString('es-ES', {day: 'numeric', month: 'short', year: 'numeric'});
  }
}
